// Copyright (c) 2019 by Michael Marucheck
//
// Use of this source code is governed by an MIT-style license that
// can be found in the LICENSE file distributed with this file.

import uuidv4 from "uuid/v4";
import React, { useState, useEffect } from "react";
import { Link, RouteComponentProps } from "react-router-dom";
import "../components/common.css";

import { ACTIVITY_STATUS, URLS } from "../urls";
import { volCache } from "../store/api";
import { update } from "../UiUtil";
import {
  acceptedAllRequiredMarkdowns,
  PendingMarkdowns,
} from "../components/PendingMarkdowns";
import { PageContainer } from "../components/PageContainer";
import { Uid, ReviewState } from "../store/schema";
import { durationString } from "../util";

type PageProps = RouteComponentProps<{}>;

export const HomePage: React.FC<PageProps> = () => {
  debug("======= IndexPage =======");
  const [state, setState] = useState(volCache().get());

  const {
    activities,
    activityReviews,
    volunteerLoaded,
    markdowns,
    organizations,
    school,
    supervisors,
    user,
    volunteer,
  } = state;

  useEffect(() => {
    return volCache().subscribe(newState => {
      debug("Updating state");
      update("setState", state, setState, newState);
    });
  });

  const approvedActivities: Uid[] = activityReviews
    .filter(x => x.data.state === ReviewState.Approved)
    .map(x => x.data.uid);

  const totalSeconds = activities
    .filter(x => !x.data.isDeleted)
    .reduce((acc, x) => acc + x.data.durationInSec, 0);
  const approvedSeconds = activities
    .filter(x => approvedActivities.some(uid => uid === x.data.uid))
    .reduce((acc, x) => acc + x.data.durationInSec, 0);

  const totalText =
    totalSeconds === 0
      ? `No activities recorded yet.`
      : `Hours recorded=${durationString(
          totalSeconds
        )} (approved=${durationString(approvedSeconds)})`;

  return (
    <PageContainer
      title="Volunteer Reporting"
      crumbs={[]}
      administrators={state.administrators}
      school={state.school}
      checks={{ user, loaded: volunteerLoaded }}
    >
      {volunteer == null || volunteer.data.schoolUid === "" ? (
        <>
          <div className="nav" key="SetUpProfileCaption">
            It looks like you haven't set up your profile yet. Let's do that
            first.
          </div>
          <div className="nav" key="ProfileLink">
            <Link to={URLS.vProfile()}>Click to edit your profile.</Link>
          </div>
        </>
      ) : !acceptedAllRequiredMarkdowns(volunteer.data, school) ? (
        <>
          <PendingMarkdowns
            volunteer={volunteer.data}
            school={school}
            markdowns={markdowns}
          />
        </>
      ) : (
        <>
          <div className="nav" key="NewActivity">
            <Link to={URLS.vActivitiesDetail(uuidv4())}>
              Enter New Activity
            </Link>
          </div>
          {activities.length > 0 && (
            <div className="nav" key="Activities">
              <Link to={URLS.vActivities(ACTIVITY_STATUS.all)}>Activities</Link>
            </div>
          )}
          {organizations.length > 0 && (
            <div className="nav" key="Organizations">
              <Link to={URLS.vOrganizations()}>Organizations</Link>
            </div>
          )}
          {supervisors.length > 0 && (
            <div className="nav" key="Supervisors">
              <Link to={URLS.vSupervisors()}>Supervisors</Link>
            </div>
          )}
          <div className="nav" key="Profile">
            <Link to={URLS.vProfile()}>Edit Profile</Link>
          </div>
          {markdowns.map(markdown => (
            <div className="nav" key={`Markdown-${markdown.data.uid}`}>
              <Link to={URLS.vMarkdown(markdown.data.uid)}>
                {markdown.data.name}
              </Link>
            </div>
          ))}
          <div className="nav" key="Summary">
            {totalText}
          </div>
        </>
      )}
    </PageContainer>
  );
};

function debug(message: string) {
  if (true) {
    console.log(`vHomePage: ${message}`);
  }
}
